import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, Empty, Popconfirm, Select, Spin, Table, Typography, message } from "antd";
import { CopyOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import copyToClipboard from "../../utils/copy";
import {
    deleteVideoReviewComment,
    listVideoReviewComments,
    listVideoReviews,
} from "../../utils/videoReviewAdminApi";
import {
    commentsToSrt,
    commentsToVtt,
    downloadTextFile,
    formatVideoTimestamp,
    sanitizeFilename,
} from "../../utils/videoReviewFormat";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";

const { Paragraph } = Typography;

const LINKS_PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

function escapeCsvCell(value) {
    const str = value == null ? "" : String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function VideoReviewResults() {
    const { t } = useTranslation();
    const [links, setLinks] = useState([]);
    const [linksTotal, setLinksTotal] = useState(0);
    const [linksPage, setLinksPage] = useState(1);
    const [linksSearch, setLinksSearch] = useState("");
    const [selectedLinkId, setSelectedLinkId] = useState("");
    const [comments, setComments] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [loadingMoreLinks, setLoadingMoreLinks] = useState(false);
    const [loadingComments, setLoadingComments] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const linksCacheRef = useRef(new Map());
    const searchDebounceRef = useRef(null);

    // 链接数量可能很大：分页从后端拉取，下拉框用 onPopupScroll 增量加载，
    // 而不是像之前那样一次性拉取全部链接列表。
    const loadLinks = useCallback(
        async (targetPage, search, { append = false } = {}) => {
            if (append) {
                setLoadingMoreLinks(true);
            } else {
                setLoadingLinks(true);
            }
            try {
                const result = await listVideoReviews({
                    page: targetPage,
                    pageSize: LINKS_PAGE_SIZE,
                    search,
                });
                result.items.forEach((item) => linksCacheRef.current.set(item.id, item));
                setLinksTotal(result.total);
                setLinksPage(result.page);
                setLinks((prev) => (append ? [...prev, ...result.items] : result.items));
            } catch {
                message.error(t("videoReview.admin.manageLoadFailed"));
            } finally {
                if (append) {
                    setLoadingMoreLinks(false);
                } else {
                    setLoadingLinks(false);
                }
            }
        },
        [t]
    );

    useEffect(() => {
        loadLinks(1, "");
    }, [loadLinks]);

    useEffect(() => {
        if (links.length > 0 && !selectedLinkId) {
            setSelectedLinkId(links[0].id);
        }
    }, [links, selectedLinkId]);

    useEffect(
        () => () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        },
        []
    );

    const handleLinkSearch = useCallback(
        (value) => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
            searchDebounceRef.current = setTimeout(() => {
                setLinksSearch(value);
                loadLinks(1, value);
            }, SEARCH_DEBOUNCE_MS);
        },
        [loadLinks]
    );

    const handleLinksPopupScroll = useCallback(
        (event) => {
            const target = event.target;
            const nearBottom =
                target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
            if (nearBottom && !loadingLinks && !loadingMoreLinks && links.length < linksTotal) {
                loadLinks(linksPage + 1, linksSearch, { append: true });
            }
        },
        [linksPage, linksSearch, linksTotal, links.length, loadingLinks, loadingMoreLinks, loadLinks]
    );

    const linkOptions = useMemo(() => {
        const options = links.map((item) => ({
            value: item.id,
            label: `${item.videoName} (${item.commentCount ?? 0})`,
        }));
        if (selectedLinkId && !links.some((item) => item.id === selectedLinkId)) {
            const cached = linksCacheRef.current.get(selectedLinkId);
            if (cached) {
                options.unshift({
                    value: cached.id,
                    label: `${cached.videoName} (${cached.commentCount ?? 0})`,
                });
            }
        }
        return options;
    }, [links, selectedLinkId]);

    // Map.get 已经是 O(1)，不需要 useMemo；且 ref 的变更不会被 useMemo 依赖感知到。
    const selectedLink = linksCacheRef.current.get(selectedLinkId) || null;

    const loadComments = useCallback(async () => {
        if (!selectedLinkId) {
            setComments([]);
            return;
        }
        setLoadingComments(true);
        try {
            const items = await listVideoReviewComments(selectedLinkId);
            setComments(items);
        } catch {
            message.error(t("videoReview.results.loadFailed"));
        } finally {
            setLoadingComments(false);
        }
    }, [selectedLinkId, t]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    const handleCopyTimestamp = useCallback(
        async (ms) => {
            try {
                await copyToClipboard(formatVideoTimestamp(ms));
                message.success(t("videoReview.results.timestampCopied"));
            } catch {
                message.error(t("videoReview.admin.copyFailed"));
            }
        },
        [t]
    );

    const handleCopyAll = useCallback(async () => {
        if (!comments.length) {
            return;
        }
        const text = comments
            .map(
                (c) =>
                    `[${formatVideoTimestamp(c.timestampMs)}] ${c.reviewerName}: ${c.body}`
            )
            .join("\n");
        try {
            await copyToClipboard(text);
            message.success(t("videoReview.results.allCopied"));
        } catch {
            message.error(t("videoReview.admin.copyFailed"));
        }
    }, [comments, t]);

    const handleExportCsv = useCallback(() => {
        if (!comments.length || !selectedLink) {
            return;
        }
        const header = [
            t("videoReview.results.colTimestamp"),
            t("videoReview.results.colReviewer"),
            t("videoReview.results.colBody"),
            t("videoReview.results.colSubmittedAt"),
        ];
        const rows = comments.map((c) => [
            formatVideoTimestamp(c.timestampMs),
            c.reviewerName,
            c.body,
            dayjs(c.createdAt * 1000).format("YYYY-MM-DD HH:mm:ss"),
        ]);
        const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\n");
        downloadTextFile(`${sanitizeFilename(selectedLink.videoName)}-comments.csv`, csv, {
            bom: true,
        });
    }, [comments, selectedLink, t]);

    const handleExportSrt = useCallback(() => {
        if (!comments.length || !selectedLink) {
            message.warning(t("videoReview.results.noComments"));
            return;
        }
        downloadTextFile(`${sanitizeFilename(selectedLink.videoName)}.srt`, commentsToSrt(comments), {
            bom: true,
        });
        message.success(t("videoReview.results.exportDone"));
    }, [comments, selectedLink, t]);

    const handleExportVtt = useCallback(() => {
        if (!comments.length || !selectedLink) {
            message.warning(t("videoReview.results.noComments"));
            return;
        }
        downloadTextFile(`${sanitizeFilename(selectedLink.videoName)}.vtt`, commentsToVtt(comments));
        message.success(t("videoReview.results.exportDone"));
    }, [comments, selectedLink, t]);

    const handleDeleteComment = useCallback(
        async (commentId) => {
            if (!selectedLinkId) {
                return;
            }
            setDeletingId(commentId);
            try {
                await deleteVideoReviewComment({ commentId, linkId: selectedLinkId });
                setComments((prev) => prev.filter((item) => item.id !== commentId));
                message.success(t("videoReview.comments.deleted"));
            } catch {
                message.error(t("videoReview.comments.deleteFailed"));
            } finally {
                setDeletingId(null);
            }
        },
        [selectedLinkId, t]
    );

    const columns = [
        {
            title: t("videoReview.results.colTimestamp"),
            dataIndex: "timestampMs",
            key: "timestampMs",
            width: 120,
            render: (ms) => (
                <Button type="link" size="small" onClick={() => handleCopyTimestamp(ms)}>
                    {formatVideoTimestamp(ms)}
                </Button>
            ),
        },
        {
            title: t("videoReview.results.colReviewer"),
            dataIndex: "reviewerName",
            key: "reviewerName",
            width: 120,
        },
        {
            title: t("videoReview.results.colBody"),
            dataIndex: "body",
            key: "body",
        },
        {
            title: t("videoReview.results.colSubmittedAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            width: 170,
            render: (ts) => dayjs(ts * 1000).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            title: t("videoReview.results.colActions"),
            key: "actions",
            width: 72,
            render: (_, record) => (
                <Popconfirm
                    title={t("videoReview.comments.deleteConfirm")}
                    okText={t("videoReview.admin.deleteOk")}
                    cancelText={t("videoReview.admin.deleteCancel")}
                    onConfirm={() => handleDeleteComment(record.id)}
                >
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingId === record.id}
                        aria-label={t("videoReview.comments.delete")}
                    />
                </Popconfirm>
            ),
        },
    ];

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("videoReview.results.title")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 640,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("videoReview.results.subtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    {loadingLinks ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : links.length === 0 ? (
                        <Empty description={t("videoReview.admin.manageEmpty")} />
                    ) : (
                        <>
                            <div className="delivery-admin-field">
                                <label>{t("videoReview.results.selectLink")}</label>
                                <Select
                                    style={{ width: "100%" }}
                                    value={selectedLinkId || undefined}
                                    onChange={setSelectedLinkId}
                                    options={linkOptions}
                                    showSearch
                                    filterOption={false}
                                    onSearch={handleLinkSearch}
                                    onPopupScroll={handleLinksPopupScroll}
                                    loading={loadingMoreLinks}
                                    notFoundContent={
                                        loadingLinks ? <Spin size="small" /> : undefined
                                    }
                                />
                            </div>

                            <div className="delivery-admin-actions" style={{ marginBottom: "1rem" }}>
                                <Button
                                    icon={<CopyOutlined />}
                                    disabled={!comments.length}
                                    onClick={handleCopyAll}
                                >
                                    {t("videoReview.results.copyAll")}
                                </Button>
                                <Button
                                    icon={<DownloadOutlined />}
                                    disabled={!comments.length}
                                    onClick={handleExportCsv}
                                >
                                    {t("videoReview.results.exportCsv")}
                                </Button>
                                <Button disabled={!comments.length} onClick={handleExportSrt}>
                                    {t("videoReview.results.exportSrt")}
                                </Button>
                                <Button disabled={!comments.length} onClick={handleExportVtt}>
                                    {t("videoReview.results.exportVtt")}
                                </Button>
                            </div>

                            <Table
                                rowKey="id"
                                columns={columns}
                                dataSource={comments}
                                loading={loadingComments}
                                pagination={{ pageSize: 20 }}
                                locale={{ emptyText: t("videoReview.results.noComments") }}
                                scroll={{ x: 720 }}
                            />
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default VideoReviewResults;
